import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Save, Trash2, Plus } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import CountryCodesPicker from "@/components/CountryCodesPicker";

const EMPTY = {
    id: "",
    name: "",
    tagline: "",
    description: "",
    image: "",
    hero_image: "",
    accent: "#001c50",
    status: "active",
    country_codes: [],
};

const IMAGE_OPTIONS = [
    "/panda/panda-insurance.png",
    "/panda/panda-travel.png",
    "/panda/panda-finance.png",
    "/panda/panda-business.png",
    "/panda/panda-lifestyle.png",
    "/panda/panda-health.png",
    "/panda/panda-super.png",
];

export default function AdminGroupDetail() {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const isNew = groupId === "new";

    const [form, setForm] = useState(EMPTY);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        api.get(`/admin/groups/${groupId}`)
            .then((r) => {
                const g = r.data;
                setForm({
                    id: g.id || "",
                    name: g.name || "",
                    tagline: g.tagline || "",
                    description: g.description || "",
                    image: g.image || "",
                    hero_image: g.hero_image || "",
                    accent: g.accent || "#001c50",
                    status: g.status || "active",
                    country_codes: Array.isArray(g.country_codes) ? g.country_codes : [],
                });
                setMeta({
                    deal_count: g.deal_count,
                    created_by: g.created_by,
                    updated_by: g.updated_by,
                });
            })
            .catch((e) => {
                toast.error(formatApiError(e));
                navigate("/admin/products");
            })
            .finally(() => setLoading(false));
    }, [groupId, isNew, navigate]);

    const set = (k) => (e) => {
        const v = e && e.target ? e.target.value : e;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const buildPayload = () => ({
        ...(isNew ? { id: form.id || undefined } : {}),
        name: form.name.trim(),
        tagline: form.tagline || null,
        description: form.description || null,
        image: form.image || null,
        hero_image: form.hero_image || form.image || null,
        accent: form.accent || "#001c50",
        status: form.status,
        country_codes: form.country_codes || [],
    });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isNew) {
                const { data } = await api.post("/admin/groups", buildPayload());
                toast.success("Product group created");
                navigate(`/admin/products/groups/${data.id}`);
            } else {
                await api.patch(`/admin/groups/${groupId}`, buildPayload());
                toast.success("Product group updated");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setConfirmDelete(false);
        try {
            await api.delete(`/admin/groups/${groupId}`);
            toast.success("Product group deleted");
            navigate("/admin/products");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const isActive = form.status === "active";

    if (loading) return <div className="p-10 text-navy-500">Loading…</div>;

    return (
        <>
            <div className="px-6 lg:px-12 pt-6">
                <Link
                    to="/admin/products"
                    data-testid="group-detail-back"
                    className="inline-flex items-center gap-2 text-sm text-navy-500 hover:text-navy-700"
                >
                    <ArrowLeft size={15} /> Back to products
                </Link>
            </div>

            <PageHeader
                eyebrow={isNew ? "New group" : `Group · ${groupId}`}
                title={isNew ? "Create a new product group" : form.name || "Edit group"}
                description={
                    isNew
                        ? "Create a group to bucket related deals (e.g. Insurance, Travel, Lifestyle)."
                        : "Rename, restyle, or deactivate this group. Deactivating hides it — and all its products — from the public site."
                }
                testid="admin-group-detail-header"
                action={
                    !isNew && (
                        <Badge
                            data-testid="group-status-badge"
                            className={
                                isActive
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 border"
                                    : "bg-red-50 text-red-700 border-red-200 border"
                            }
                        >
                            {isActive ? "Live" : "Hidden"}
                        </Badge>
                    )
                }
            />

            <div className="px-6 lg:px-12 py-8">
                <form
                    onSubmit={submit}
                    className="grid lg:grid-cols-[1fr_320px] gap-8 items-start"
                    data-testid="admin-group-form"
                >
                    <div className="rounded-2xl bg-white shadow-soft p-8 space-y-6">
                        <div className="rounded-xl bg-cream p-5 flex items-start justify-between gap-4">
                            <div>
                                <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-1">
                                    Visibility
                                </div>
                                <div className="text-navy-700 font-medium">
                                    {isActive
                                        ? "Active — visible on the public site"
                                        : "Inactive — hidden from the public site"}
                                </div>
                                <div className="text-xs text-navy-500 mt-1">
                                    When inactive, this group and every product inside it disappear from the public catalogue immediately.
                                </div>
                            </div>
                            <Switch
                                data-testid="group-status-toggle"
                                checked={isActive}
                                onCheckedChange={(v) =>
                                    setForm((f) => ({ ...f, status: v ? "active" : "inactive" }))
                                }
                                className="data-[state=checked]:bg-emerald-600 data-[state=unchecked]:bg-navy-100"
                            />
                        </div>

                        <CountryCodesPicker
                            value={form.country_codes}
                            onChange={(codes) => setForm((f) => ({ ...f, country_codes: codes }))}
                            label="Available in"
                            hint="Restrict this group to specific markets. Untick everything to publish globally."
                            testid="group-country-codes"
                        />

                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Name" required>
                                <Input
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    data-testid="group-field-name"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            {isNew && (
                                <Field label="Custom ID (optional)">
                                    <Input
                                        value={form.id}
                                        onChange={set("id")}
                                        placeholder="e.g. grp-wellness"
                                        data-testid="group-field-id"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 font-mono text-sm"
                                    />
                                </Field>
                            )}
                        </div>

                        <Field label="Tagline">
                            <Input
                                value={form.tagline}
                                onChange={set("tagline")}
                                placeholder="e.g. Protect what matters most"
                                data-testid="group-field-tagline"
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                            />
                        </Field>

                        <Field label="Description">
                            <Textarea
                                rows={3}
                                value={form.description}
                                onChange={set("description")}
                                data-testid="group-field-description"
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500"
                            />
                        </Field>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Accent colour">
                                <div className="mt-2 flex gap-3 items-center">
                                    <input
                                        type="color"
                                        value={form.accent}
                                        onChange={set("accent")}
                                        data-testid="group-field-accent"
                                        className="h-11 w-14 rounded-lg border border-navy-100 cursor-pointer bg-white"
                                    />
                                    <Input
                                        value={form.accent}
                                        onChange={set("accent")}
                                        className="bg-white border-navy-100 focus-visible:ring-gold-500 h-11 font-mono"
                                    />
                                </div>
                            </Field>
                            <Field label="Hero image URL">
                                <Input
                                    value={form.hero_image}
                                    onChange={set("hero_image")}
                                    placeholder="/panda/hero-insurance.png"
                                    data-testid="group-field-hero-image"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 text-sm"
                                />
                            </Field>
                        </div>

                        <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
                            {!isNew && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setConfirmDelete(true)}
                                    data-testid="group-delete"
                                    className="rounded-full border-red-200 text-red-600 hover:bg-red-50 h-11"
                                >
                                    <Trash2 size={15} className="mr-2" /> Delete group
                                </Button>
                            )}
                            <div className="ml-auto flex items-center gap-3">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => navigate("/admin/products")}
                                    className="text-navy-500"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="group-save"
                                    className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                                >
                                    {isNew ? (
                                        <>
                                            <Plus size={15} className="mr-2" />
                                            {saving ? "Creating…" : "Create group"}
                                        </>
                                    ) : (
                                        <>
                                            <Save size={15} className="mr-2" />
                                            {saving ? "Saving…" : "Save changes"}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <aside className="rounded-2xl bg-white shadow-soft p-6 space-y-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                                Group image
                            </div>
                            {form.image && (
                                <div
                                    className="rounded-xl p-4 mb-3 flex items-center justify-center"
                                    style={{ background: `${form.accent}12` }}
                                >
                                    <img
                                        src={form.image}
                                        alt=""
                                        className="h-32 w-32 object-contain"
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-3 gap-2">
                                {IMAGE_OPTIONS.map((src) => (
                                    <button
                                        key={src}
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, image: src }))}
                                        data-testid={`group-image-${src.split("/").pop().replace(".png", "")}`}
                                        className={`rounded-lg p-2 border-2 transition-colors ${
                                            form.image === src
                                                ? "border-gold-500 bg-gold-500/10"
                                                : "border-transparent hover:border-navy-100"
                                        }`}
                                    >
                                        <img
                                            src={src}
                                            alt=""
                                            className="h-12 w-12 object-contain mx-auto"
                                        />
                                    </button>
                                ))}
                            </div>
                            <Input
                                placeholder="Or paste an image URL"
                                value={form.image}
                                onChange={set("image")}
                                data-testid="group-field-image-url"
                                className="mt-3 bg-white border-navy-100 focus-visible:ring-gold-500 h-10 text-sm"
                            />
                            <div className="mt-3">
                                <ImageUploader
                                    testid="group-image-upload"
                                    label="Upload your own image"
                                    onUploaded={(url) =>
                                        setForm((f) => ({
                                            ...f,
                                            image: url,
                                            hero_image: f.hero_image || url,
                                        }))
                                    }
                                />
                                <p className="text-[11px] text-navy-500 mt-1.5 text-center">
                                    PNG / JPG / WebP · max 5 MB
                                </p>
                            </div>
                        </div>

                        {!isNew && (
                            <div className="text-xs text-navy-500 pt-4 border-t border-navy-100/70 space-y-1">
                                <div>
                                    ID: <span className="font-mono text-navy-700">{form.id}</span>
                                </div>
                                {meta.deal_count !== undefined && (
                                    <div>{meta.deal_count} active product(s) in this group</div>
                                )}
                                {meta.created_by && <div>Created by {meta.created_by}</div>}
                                {meta.updated_by && <div>Last updated by {meta.updated_by}</div>}
                            </div>
                        )}
                    </aside>
                </form>
            </div>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this group?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes the group. You can only delete a group
                            with no products attached — otherwise deactivate it to hide it
                            from the public site.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="group-delete-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            data-testid="group-delete-confirm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            {children}
        </div>
    );
}
